import React, {
  FunctionComponent,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';
import withStyles from 'react-jss';
import { map, pathOr, pipe, reject, uniqBy, isNil } from 'ramda';

import Button from 'components/Button';
import OfferDetailsPlaceholder from 'components/Placeholder/presets/OfferDetailsPlaceholder';
import { ClassProp } from 'helpers/types';
import { Checkbox, CheckboxProps } from 'semantic-ui-react';

import {
  Segment,
  SegmentType,
  SegmentAction,
  Throttle,
  OfferSummary,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';

import { AttachedAudiences } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/SelectAudience/SelectAudience';
import styles from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/scenes/PulseOffer/PulseOffer.styles';

import NewOfferSegment from './components/NewOfferSegment';
import OfferSegmentsTable from '../OfferSegmentsTable';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  segments?: Segment[];
  offer?: OfferSummary;
};

export type ExtendedSegment = Segment & {
  expired: boolean;
};

const isExpired = (segment: Segment, currentDate: Date) =>
  segment.endDate === null
    ? false
    : !(segment.endDate && new Date(segment.endDate) > currentDate);

const isDeleted = (segment: Segment) => segment.startDate === segment.endDate;

const sortSegmentsByDateThenPriority = (segments: ExtendedSegment[]) => {
  segments.sort((a, b) => {
    const segmentDateA = new Date(a.startDate);
    const segmentDateB = new Date(b.startDate);

    if (segmentDateA === segmentDateB) {
      return a.segmentPriority - b.segmentPriority;
    }
    return segmentDateA < segmentDateB ? 1 : -1;
  });
  return segments;
};

const segmentsWithExpiredProp = (segments: Segment[]) => {
  const currentDate = new Date();
  const extendedSegments: ExtendedSegment[] = segments.map((segment) => {
    const extendedSegment: ExtendedSegment = {
      ...segment,
      expired: isExpired(segment, currentDate),
      deleted: isDeleted(segment),
    };
    return extendedSegment;
  });

  // Ignore deleted segments from both lists
  const expiredOffers = extendedSegments.filter(
    (segment) => segment.expired && !segment.deleted,
  );
  const nonExpiredOffers = extendedSegments.filter(
    (segment) => !segment.expired && !segment.deleted,
  );

  return {
    nonExpiredOffers: sortSegmentsByDateThenPriority(nonExpiredOffers),
    expiredOffers: sortSegmentsByDateThenPriority(expiredOffers),
  };
};

const OfferSegments: FunctionComponent<Props> = ({
  classes,
  segments,
  offer,
}) => {
  // attachedAudiences: A hash of the segment's audiences keyed off their UUID and storing active date ranges
  const [attachedAudiences, setAttachedAudiences] = useState<AttachedAudiences>(
    {},
  );

  const [offerSegments, setOfferSegments] = useState<ExtendedSegment[]>([]);
  const [nonExpiredOffers, setNonExpiredOffers] = useState<ExtendedSegment[]>(
    [],
  );

  const [attachedThrottles, setAttachedThrottles] = useState<Throttle[]>([]);

  useEffect(() => {
    if (segments) {
      const { nonExpiredOffers, expiredOffers } = segmentsWithExpiredProp(
        segments,
      );
      const offerSegments = [...nonExpiredOffers, ...expiredOffers];
      setOfferSegments(offerSegments);
      setNonExpiredOffers(nonExpiredOffers);
      const uniqueThrottles = pipe(
        map((segment: ExtendedSegment) => segment.throttle),
        reject(isNil),
        uniqBy((throttle) => throttle.uuid),
      )(offerSegments) as Throttle[];
      setAttachedThrottles(uniqueThrottles);
    }
  }, [segments]);

  const [creatingSegment, setCreatingSegment] = useState<boolean>(false);
  const [showExpiredOffers, setShowExpiredOffers] = useState(
    nonExpiredOffers.length !== 0,
  );
  const [activeSegment, setActiveSegment] = useState<SegmentAction>({
    segmentUuid: null,
    action: null,
  });

  useEffect(() => {
    const newAttachedAudiences = {} as AttachedAudiences;

    offerSegments
      .filter(
        (segment) =>
          segment.endDate &&
          segment.audience &&
          new Date() < new Date(segment.endDate),
      )
      .filter((segment) => segment.audience !== undefined)
      .forEach((segment) => {
        const audienceUuid = segment?.audience?.audienceUuid;
        if (audienceUuid && segment.endDate) {
          if (newAttachedAudiences[audienceUuid]) {
            newAttachedAudiences[audienceUuid].push([
              segment.startDate,
              segment.endDate,
            ]);
          } else {
            newAttachedAudiences[audienceUuid] = [
              [segment.startDate, segment.endDate],
            ];
          }
        }
      });
    setAttachedAudiences(newAttachedAudiences);
  }, [offerSegments]);

  if (!offerSegments) {
    return null;
  }

  const baseSegments = offerSegments.filter(
    (s) => s.segmentType === SegmentType.BASE,
  );

  const baseIOTickets = baseSegments
    .map((segment) => {
      const items = pathOr(
        [],
        ['financialTerm', 'financialTermItems'],
        segment,
      );
      return items.map((fti: any) => fti.insertionOrderTicket);
    })
    .reduce((acc, cur) => acc.concat(cur), []);

  const disablePulse = baseIOTickets.some((t: any) => t !== baseIOTickets[0]);

  const closeNewOffer = () => {
    setCreatingSegment(false);
  };

  const openNewOffer = () => {
    setCreatingSegment(true);
  };

  const handleShowExpiredOffers = (
    _event: SyntheticEvent<HTMLElement, Event>,
    data: CheckboxProps,
  ) => {
    setShowExpiredOffers(data.checked ?? false);
  };

  if (segments === undefined) {
    return (
      <>
        <h2 className={classes.topHeader}>Segments</h2>
        <OfferDetailsPlaceholder />
      </>
    );
  }

  return (
    <>
      <div className={classes.segmentHeader}>
        <h2 className={classes.topHeader}>Segments</h2>
        <Checkbox
          className={classes.showExpiredOffers}
          toggle
          checked={showExpiredOffers}
          onChange={handleShowExpiredOffers}
          label="Show expired segments"
        />
        <Button
          className={classes.addSegmentButton}
          title="Add Segment"
          onClick={openNewOffer}
        />
      </div>
      <ul className={classes.segmentList}>
        {creatingSegment && (
          <li className={classes.segmentListItem}>
            <NewOfferSegment
              attachedThrottles={attachedThrottles}
              attachedAudiences={attachedAudiences}
              onCancel={closeNewOffer}
              onSuccess={closeNewOffer}
              insertionOrderRef={baseIOTickets[0]}
              baseSegment={baseSegments[0]}
            />
          </li>
        )}
      </ul>
      <OfferSegmentsTable
        segments={showExpiredOffers ? offerSegments : nonExpiredOffers}
        offer={offer}
        disablePulse={disablePulse}
        activeSegment={activeSegment}
        setActiveSegment={setActiveSegment}
        attachedAudiences={attachedAudiences}
      />
    </>
  );
};

export default withStyles(styles)(OfferSegments);
